import { useState, useMemo } from 'react'

import { TreeItemIndex } from 'react-complex-tree'

import { useI18n } from '@tribeplatform/react-components/i18n'

import {
  COLLECTIONS_INDEX,
  createCollectionsRootTreeItem,
} from '../tree-items/createCollectionRootTreeItem.js'
import {
  collectionTreeItemIndex,
  createCollectionTreeItem,
} from '../tree-items/createCollectionTreeItem.js'
import {
  createLoadingTreeItem,
  LOADING_INDEX,
} from '../tree-items/createLoadingTreeItem.js'
import {
  NO_COLLECTION_INDEX,
  createNoCollectionTreeItem,
} from '../tree-items/createNoCollectionTreeItem.js'
import { TreeItems } from '../types.js'

import { useCollectionsWithSpaceQuery } from './useCollectionsWithSpaceQuery.js'
import { useLimitTreeItems } from './useLimitTreeItems.js'

type CollectionId = string
type CollectionSpacesTreeItems = Record<CollectionId, TreeItems>

export type UseCollectionTreeItemsProps = {
  query?: string
  activeCollectionIndex?: TreeItemIndex
  activeSpaceIndex?: TreeItemIndex
}

export function useCollectionTreeItems({
  query,
  activeCollectionIndex,
  activeSpaceIndex,
}: UseCollectionTreeItemsProps) {
  const intl = useI18n()
  const [lazyCollectionSpaces, setLazyCollectionSpaces] =
    useState<CollectionSpacesTreeItems>({})

  const {
    collections: unsortedCollections,
    isLoading,
    noCollectionSpaceTreeItems,
    nameMatchedCollectionIds,
    searchCollectionSpaces,
  } = useCollectionsWithSpaceQuery({
    query,
    activeSpaceTreeIndex: activeSpaceIndex,
  })

  // Merge search-derived spaces with lazy-loaded spaces.
  // Name-matched collections use lazy loading (show all spaces),
  // other collections use search results (show only matching spaces).
  const collectionSpaces = useMemo(() => {
    if (!searchCollectionSpaces) {
      return lazyCollectionSpaces
    }
    const result: CollectionSpacesTreeItems = {}
    for (const [id, items] of Object.entries(searchCollectionSpaces)) {
      if (!nameMatchedCollectionIds.has(id)) {
        result[id] = items
      }
    }
    for (const [id, items] of Object.entries(lazyCollectionSpaces)) {
      if (nameMatchedCollectionIds.has(id)) {
        result[id] = items
      }
    }
    return result
  }, [searchCollectionSpaces, lazyCollectionSpaces, nameMatchedCollectionIds])

  const collections = useMemo(
    () =>
      [...(unsortedCollections ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { ignorePunctuation: true }),
      ),
    [unsortedCollections],
  )

  const collectionIndexes = useMemo(() => {
    return collections?.map(c => collectionTreeItemIndex(c.id)) ?? []
  }, [collections])
  const limitedCollections = useLimitTreeItems(
    'collections',
    collectionIndexes,
    {
      ensureVisible: activeCollectionIndex,
    },
  )

  const noCollectionSpaceKeys = useMemo(
    () => Object.keys(noCollectionSpaceTreeItems),
    [noCollectionSpaceTreeItems],
  )

  const hasNoCollectionSpaces = noCollectionSpaceKeys.length > 0

  const collectionChildrenItems = useMemo(
    () => [
      ...(isLoading ? [LOADING_INDEX] : []),
      ...limitedCollections.children,
      ...(hasNoCollectionSpaces ? [NO_COLLECTION_INDEX] : []),
    ],
    [limitedCollections.children, isLoading, hasNoCollectionSpaces],
  )

  const collectionTreeItems: TreeItems = useMemo(() => {
    const loadingTreeItem = createLoadingTreeItem(intl)
    const collectionRootItem = {
      [COLLECTIONS_INDEX]: createCollectionsRootTreeItem(intl, {
        children: collectionChildrenItems,
      }),
    }

    if (isLoading) {
      return {
        ...collectionRootItem,
        ...loadingTreeItem,
      }
    }

    const collectionTreeItems = collections.reduce((acc, c) => {
      acc[collectionTreeItemIndex(c.id)] = createCollectionTreeItem({
        collection: c,
        children: Object.keys(collectionSpaces[c.id] ?? {}),
        setCollectionSpacesTreeItems: setLazyCollectionSpaces,
        enableQuery: context =>
          context.isExpanded &&
          (!(query.length > 0) || nameMatchedCollectionIds.has(c.id)),
      })
      return acc
    }, {} as TreeItems)

    const spaceTreeItems = Object.values(collectionSpaces).reduce((acc, c) => {
      return {
        ...acc,
        ...c,
      }
    }, {})

    const noCollectionItems = hasNoCollectionSpaces
      ? {
          [NO_COLLECTION_INDEX]: createNoCollectionTreeItem(intl, {
            children: noCollectionSpaceKeys,
          }),
          ...noCollectionSpaceTreeItems,
        }
      : {}

    return {
      ...collectionRootItem,
      ...collectionTreeItems,
      ...limitedCollections.items,
      ...noCollectionItems,
      ...spaceTreeItems,
    }
  }, [
    intl,
    collections,
    isLoading,
    collectionSpaces,
    collectionChildrenItems,
    query,
    nameMatchedCollectionIds,
    noCollectionSpaceTreeItems,
    noCollectionSpaceKeys,
    hasNoCollectionSpaces,
    limitedCollections.items,
  ])

  return {
    collectionIndex: COLLECTIONS_INDEX,
    collectionTreeItems,
  }
}
